import { useCallback, useEffect, useRef, useState } from 'react';
import {
  decideQueueAdd,
  queueActs,
  queueHoldsVideo,
  type QueueActs,
  type QueueState,
  type QueueVideo,
} from '@visious/core';
import { navigate } from '../router/router';
import { usePlayback } from '../playback/context';
import { useSettings } from '../platform/settings';
import { describeWriteProblem } from '../playlists/model';
import { useEntryMutations } from '../playlists/mutations';
import { queueEntryHref, toQueueVideo, type QueueCandidate } from './model';
import {
  appendToQueue,
  clearQueue,
  playNextInQueue,
  readQueue,
  removeVideoFromQueue,
  startQueue,
  useQueue,
} from './store';
import { useCopy } from '../strings/useCopy';

/** What the last press said, for the ~2.5 s a `role="status"` line stays up. `null` when silent. */
export type QueueFeedback = string | null;

export interface QueueActions {
  /**
   * The **Add to queue** press. One meaning and nothing to answer: the video goes on the **end**.
   *
   * Which list's end depends on what the queue is. A list of the reader's own takes the card's own row,
   * so the entry is built from what is already on screen and no request goes out (`queue/model.ts`). A
   * queue that is a window on a playlist takes the video into **that playlist**, which is a request and
   * can be refused.
   */
  readonly add: (item: QueueCandidate) => void;
  /**
   * A plain press on the video itself, once a queue exists. The pressed card takes over its own
   * picture with the two answers (`queue/QueueCardAsk.tsx`); nothing about the queue has changed yet.
   */
  readonly askAbout: (item: QueueCandidate) => void;
  /**
   * Whether *this* card is the one being asked about, which is how one card in a grid of forty knows
   * to draw the two answers over its picture while the other thirty-nine stay as they were.
   *
   * **Video id and href together.** Neither alone is a card: a grid can draw the same video at two
   * hrefs (a search result and a playlist entry on one page), and two different videos obviously
   * share no id — while a grid never draws one video twice at the same href, which is what makes the
   * pair unique across every screen that intercepts a press. The one list that *can* hold a video
   * twice over is the queue's own page, and no press is intercepted there at all.
   */
  readonly asking: (item: QueueCandidate) => boolean;
  /**
   * Which edits this list can be asked for, straight off `queueActs` (core, tested): a list of the
   * reader's own takes all four, while a linked one is a playlist, so the two acts that move rows stop
   * existing and the other two follow whether this account can write to that playlist.
   *
   * Read **during a render**, because it decides whether a card draws a queue button at all, so it
   * comes off the subscribed snapshot exactly as {@link holds} does.
   */
  readonly acts: QueueActs;
  /**
   * Whether the queue already holds this video, which is what turns a card's Add into a Remove. It
   * asks about the *video* because a card has no row in mind — see {@link remove}.
   */
  readonly holds: (videoId: string) => boolean;
  /**
   * Whether a change to this video is on its way to the playlist, which is the in-flight face a card's
   * button wears and the press it declines. Always `false` while the queue is a list of the reader's
   * own: that edit is an array and there is nothing to wait for.
   */
  readonly busyOf: (videoId: string) => boolean;
  /**
   * Take this video out of the list: every copy of it, in one press and with no confirm. On a list of
   * the reader's own nothing is sent anywhere and the same button puts it back, which is the whole
   * reason the app's confirm grammar is not spent here. On a linked queue it is the playlist's own
   * removal, one request per copy the list holds.
   */
  readonly remove: (item: QueueCandidate) => void;
  /** The card whose picture is currently asking. `null` when no card is. */
  readonly pending: QueueCandidate | null;
  /** Drop the list and open the video on its own. The answer that ends the queue. */
  readonly playNow: () => void;
  /**
   * Next in line, and stay where you are. Declines while the queue is a window on a playlist, which is
   * an act this API has no endpoint for — the button that reaches it is disabled there and says why
   * (`QueueCardAsk.tsx`).
   */
  readonly playNext: () => void;
  /** Put the card back the way it was: no queue change, corner buttons returned. */
  readonly dismiss: () => void;
  readonly feedback: QueueFeedback;
  /**
   * Say what an act on the list did, on the one line the app keeps for it
   * (`queue/QueueActionsHost.tsx`). Here rather than in each control because there is one such line: a
   * second `role="status"` node would announce over this one, and a reader hearing two sentences about
   * one press cannot tell which of them is the answer.
   *
   * The panel's Save control is the caller outside this hook (`queue/useSaveQueue.ts`), and it is a
   * caller for the same reason the presses above report at all: what it changed is a playlist on the
   * instance, which is somewhere the reader is not looking.
   */
  readonly report: (message: string) => void;
}

/**
 * Everything a press on a card can do to the queue, in one hook, so every grid in the app gets the
 * identical flow from one call.
 *
 * The **decisions** are core's and tested — `decideQueueAdd` for the button, `queuePlayNext` for one
 * of the two answers a press offers, `queueActs` for which of them the list will take at all. What is
 * here is the four things they need that core cannot see: which video a player is on, what the reader
 * set "Start playing on arrival" to, the whole record of the video the queue has to be built *around*,
 * and the requests a linked queue's edits are made of.
 *
 * **A linked queue's edits are the playlist's edits.** The rows a reader sees are that playlist's rows
 * (`core/queue/model.ts`'s `QueueLink`), so an Add is a `POST` to it and a Remove a `DELETE` from it,
 * and touching the array as well would leave the two disagreeing until the next page landed.
 */
export function useQueueActions(): QueueActions {
  const queue = useQueue();
  const settings = useSettings();
  const playback = usePlayback();
  const entries = useEntryMutations();
  const words = useCopy().text.queue.add;
  const [pending, setPending] = useState<QueueCandidate | null>(null);
  const [feedback, setFeedbackText] = useState<QueueFeedback>(null);
  const [writing, setWriting] = useState<ReadonlySet<string>>(NONE_WRITING);

  /*
   * The outcome line, and its own clock. The Share dropdown's ~2.5 s, since it is the same kind of
   * statement in the same kind of place: something happened somewhere the reader is not looking, said
   * once and then withdrawn. The timer is cleared and restarted per press, or a second press inside
   * the window would be wiped by the first one's expiry.
   */
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setFeedback = useCallback((message: string) => {
    if (feedbackTimer.current !== null) clearTimeout(feedbackTimer.current);
    setFeedbackText(message);
    feedbackTimer.current = setTimeout(() => setFeedbackText(null), FEEDBACK_MS);
  }, []);
  useEffect(
    () => () => {
      if (feedbackTimer.current !== null) clearTimeout(feedbackTimer.current);
    },
    [],
  );

  /*
   * What the corner player (or the watch page) is on right now, as a queue entry.
   *
   * `usePlayback()` is the session, which is the only place this is knowable: the reader may be on
   * /watch, or three pages away with the video in the corner, and both are "something is playing" for
   * the purpose of building a queue around it. The *title* comes from the binding's loaded video
   * rather than from anywhere else, because that is the one copy of it this app is sure about.
   */
  const currentVideo = useCallback((): QueueVideo | null => {
    const video = playback?.binding.video ?? null;
    if (video === null) return null;
    return {
      videoId: playback?.binding.videoId ?? video.videoId,
      title: video.title,
      author: video.author,
      authorHref: video.authorHref,
      thumbnailUrl: video.thumbnailUrl,
      durationSec: video.durationSec,
    };
  }, [playback]);

  /*
   * Which videos have a change on its way to the playlist. Kept per **video** rather than read off
   * `useEntryMutations`'s own set, which keys an add by playlist id and would therefore put the
   * in-flight face on every card in the grid for the length of one press — `playlists/useWatchLater.ts`
   * keeps its own for exactly that reason. A card's Remove takes every copy the list holds, so one key
   * covers the whole run of removals it sends.
   */
  const track = useCallback(async <T,>(videoId: string, work: () => Promise<T>): Promise<T> => {
    setWriting((previous) => new Set(previous).add(videoId));
    try {
      return await work();
    } finally {
      setWriting((previous) => {
        const next = new Set(previous);
        next.delete(videoId);
        return next;
      });
    }
  }, []);

  /*
   * The add a linked queue makes: the video on the end of the **playlist**, and nothing done to the
   * array. `useEntryMutations` invalidates both of the playlist's read keys, so the loader refetches and
   * the projection picks the new row up by itself (`queue/store.ts`'s `projectPlaylistQueue`).
   *
   * Two things follow that the copy has to be straight about. The video lands at the **end** of the
   * playlist, so a run that has not read that far shows no new row and the outcome line says where the
   * video went instead of promising one. And the press can be **refused**, which no press on this button
   * could be before, so the same `role="status"` line carries the reason.
   *
   * An invalidation refetches every page an infinite query has in hand, so an add made with 40 pages of
   * a long playlist loaded costs those 40 pages again. That is the playlist read's existing behaviour
   * everywhere in the app, and it is not this control's to change.
   */
  const addToPlaylist = useCallback(
    async (playlistId: string, videoId: string): Promise<void> => {
      const result = await track(videoId, () => entries.add(playlistId, videoId));
      if (result.ok) setFeedback(words.addedPlaylistEnd);
      else setFeedback(words.addRefused(describeWriteProblem(result.problem, 'add').title));
    },
    [entries, setFeedback, track, words],
  );

  /*
   * The removal a linked queue makes: one request per copy of that video the list holds.
   *
   * A card names a *video* and a playlist can hold one twice — §7i measures the same `videoId` posted
   * twice answering `201` twice — so "take this out of the list" is as many `DELETE`s as there are rows.
   * `indexId` is the only key a removal has and a mirrored YouTube list's rows carry none, so a row
   * without one is left out of the run rather than guessed at, and a video whose every row is keyless
   * sends nothing at all instead of reporting a removal that never happened.
   *
   * One at a time, because each removal renumbers every later `index` on the server (§7i) and each fires
   * its own invalidation. The rows leave the panel when the refetch lands: applying a removal to the run
   * before then is the loader's own bookkeeping (`screens/watch/useWatchPlaylist.ts`), and it is keyed to
   * the rows that loader removed itself.
   */
  const removeFromPlaylist = useCallback(
    async (state: QueueState, playlistId: string, videoId: string): Promise<void> => {
      const keys = state.entries
        .filter((entry) => entry.videoId === videoId)
        .map((entry) => entry.indexId)
        .filter((indexId): indexId is string => indexId !== null);
      if (keys.length === 0) return;
      await track(videoId, async () => {
        for (const indexId of keys) {
          const result = await entries.remove(playlistId, indexId);
          if (result.ok) continue;
          setFeedback(words.removeRefused(describeWriteProblem(result.problem, 'remove').title));
          return;
        }
        setFeedback(words.removedPlaylist);
      });
    },
    [entries, setFeedback, track, words],
  );

  const add = useCallback(
    (item: QueueCandidate) => {
      const state = readQueue();
      /*
       * The link is asked **before** `decideQueueAdd`, and that order is the whole of this arm: that
       * function answers `append` because the list has rows, and a linked queue whose first page has not
       * landed yet has none — so an unguarded call would start a fresh list of the reader's own and throw
       * the link away with it, leaving a queue of one video beside a `list` in the address bar.
       */
      if (state.link !== null) {
        if (queueActs(state).add) void addToPlaylist(state.link.playlistId, item.videoId);
        return;
      }
      const action = decideQueueAdd({
        queue: state,
        videoId: item.videoId,
        // The **binding's** id rather than the loaded record's: it is known from the moment a
        // session exists, so a press that lands while the video's own payload is still in flight
        // still knows whether the card and the player name the same video.
        playingVideoId: playback?.binding.videoId ?? null,
        autoplay: settings.autoplay,
      });
      const video = toQueueVideo(item);
      if (action.kind === 'append') {
        appendToQueue([video]);
        setFeedback(words.addedEnd);
        return;
      }
      if (action.kind === 'adoptCurrent') {
        // The card is the video already on screen, so the queue becomes that one video and the
        // playback is left strictly alone: `false` keeps the store from asking for a play, and the
        // retained session wins over an identical queue entry in `playback/model.ts` — which is
        // what keeps the picture from reloading under an unchanged video id.
        startQueue([video], false);
        setFeedback(words.startedWithPlaying);
        return;
      }
      if (action.kind === 'startWithCurrent') {
        const playingNow = currentVideo();
        // The playing video leads the list so that nothing about the playback changes: the cursor
        // lands on entry 1, which is the video that is already on screen. A player whose own record
        // has not loaded yet is the one case where there is nothing to lead with, and starting the
        // queue at the pressed video is then the honest answer rather than a blank first row.
        // Never plays: the press said "after this", and the video already on screen is entry 1, so
        // starting it would be starting what is already running.
        startQueue(playingNow === null ? [video] : [playingNow, video], false);
        setFeedback(words.addedEnd);
        return;
      }
      startQueue([video], action.play);
      // The corner player appears either way (`playback/PlaybackHost.tsx`); what the setting decides
      // is whether it starts. Saying which of the two happened is the whole value of this line — a
      // queue that appeared silently and did not play reads as a press that failed.
      setFeedback(action.play ? words.started : words.startedPaused);
    },
    [addToPlaylist, currentVideo, playback, setFeedback, settings.autoplay, words],
  );

  const askAbout = useCallback((item: QueueCandidate) => {
    setPending(item);
  }, []);

  /*
   * Which card is asking. The pair rather than either half is argued on {@link QueueActions.asking};
   * the short version is that a video id names a video and an href names a place, and only the two
   * together name a card.
   */
  const asking = useCallback(
    (item: QueueCandidate) =>
      pending !== null && pending.videoId === item.videoId && pending.href === item.href,
    [pending],
  );

  /*
   * Read off the subscribed `queue` rather than through `readQueue()`, unlike the press handlers
   * above: this one is asked **during a render** — it decides which of two buttons a card draws — so
   * it has to be the value this render was scheduled for, not whatever the store holds by the time
   * React gets here.
   */
  const holds = useCallback((videoId: string) => queueHoldsVideo(queue, videoId), [queue]);

  const remove = useCallback(
    (item: QueueCandidate) => {
      const state = readQueue();
      if (state.link !== null) {
        if (queueActs(state).remove) {
          void removeFromPlaylist(state, state.link.playlistId, item.videoId);
        }
        return;
      }
      removeVideoFromQueue(item.videoId);
      setFeedback(words.removed);
    },
    [removeFromPlaylist, setFeedback, words],
  );

  const dismiss = useCallback(() => setPending(null), []);

  const playNext = useCallback(() => {
    if (pending === null) return;
    // The live list rather than the render's, like every other press here, and `queueActs` rather than a
    // rule restated: a playlist has no act that puts a video next, so the button offering it is disabled
    // while the queue is a window on one and this declines for the press that reaches it anyway.
    if (!queueActs(readQueue()).playNext) return;
    playNextInQueue(toQueueVideo(pending));
    setPending(null);
    setFeedback(words.addedNext);
  }, [pending, setFeedback, words]);

  const playNow = useCallback(() => {
    if (pending === null) return;
    const href = pending.href;
    setPending(null);
    // Cleared *before* the navigation, and that ordering is the whole of the act: the watch page the
    // reader lands on must not find a queue still holding the old list and put its panel back up
    // beside the video they asked to watch on its own. The **card's own href**, `list=` and all, so a
    // playlist entry opens inside its playlist rather than as a lone video.
    clearQueue();
    navigate(href);
  }, [pending]);

  return {
    add,
    askAbout,
    asking,
    // Computed here rather than inside a callback, for `holds`'s reason: a card asks it while it renders,
    // so it has to be the list this render was scheduled for.
    acts: queueActs(queue),
    holds,
    busyOf: (videoId: string) => writing.has(videoId),
    remove,
    pending,
    playNow,
    playNext,
    dismiss,
    feedback,
    report: setFeedback,
  };
}

/** The Share dropdown's own window: long enough to read one sentence, short enough not to linger. */
const FEEDBACK_MS = 2500;

/** Stable, not rebuilt per render: a new empty set every time would re-render every card. */
const NONE_WRITING: ReadonlySet<string> = new Set<string>();

/** Where a queue entry plays. Re-exported so a grid needs one import for the whole flow. */
export { queueEntryHref };
